import React, { useEffect, useState, memo } from 'react';
import { ArrowDown, Star } from 'lucide-react';
import { FadeIn } from './ui/FadeIn';
import { Button } from './ui/Button';
function useCountdown() {
  const getDeadline = () => {
    const stored = localStorage.getItem('memorial-deadline');
    if (stored) {
      const deadline = parseInt(stored, 10);
      if (deadline > Date.now()) return deadline;
    }
    // Set deadline to 23h 59m 59s from now (rolling ~24h window)
    const deadline = Date.now() + 24 * 60 * 60 * 1000 - 1000;
    localStorage.setItem('memorial-deadline', String(deadline));
    return deadline;
  };
  const [deadline] = useState(getDeadline);
  const [timeLeft, setTimeLeft] = useState(() => Math.max(0, deadline - Date.now()));
  useEffect(() => {
    const interval = setInterval(() => {
      const remaining = Math.max(0, deadline - Date.now());
      setTimeLeft(remaining);
      if (remaining <= 0) clearInterval(interval);
    }, 1000);
    return () => clearInterval(interval);
  }, [deadline]);
  const hours = Math.floor(timeLeft / (1000 * 60 * 60));
  const minutes = Math.floor(timeLeft % (1000 * 60 * 60) / (1000 * 60));
  const seconds = Math.floor(timeLeft % (1000 * 60) / 1000);
  return {
    hours: String(hours).padStart(2, '0'),
    minutes: String(minutes).padStart(2, '0'),
    seconds: String(seconds).padStart(2, '0')
  };
}
export function HeroSection() {
  const countdown = useCountdown();
  const scrollToNext = () => {
    window.scrollTo({
      top: window.innerHeight,
      behavior: 'smooth'
    });
  };
  return <section className="relative min-h-screen flex flex-col items-center justify-center pt-14 pb-8 px-6 bg-base overflow-hidden" data-id="element-343">
      {/* Subtle background glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-accent-primary/5 rounded-full blur-[120px] pointer-events-none" data-id="element-344" />

      <div className="max-w-5xl mx-auto w-full flex flex-col items-center text-center z-10 mt-2" data-id="element-345">
        <FadeIn delay={0.2} data-id="element-346">
          <span className="inline-block font-ui text-xs font-medium tracking-widest uppercase text-accent-soft border border-accent-muted rounded-full px-4 py-1 mb-5" data-id="element-347">
            Memorial Edition
          </span>
        </FadeIn>

        <FadeIn delay={0.4} data-id="element-348">
          <h1 className="font-display text-4xl md:text-6xl lg:text-7xl font-bold text-text-primary leading-tight tracking-tight mb-4 max-w-4xl mx-auto" data-id="element-349">
            The Voice They Couldn't Silence
          </h1>
        </FadeIn>

        <FadeIn delay={0.6} data-id="element-350">
          <p className="font-body text-lg md:text-xl text-text-secondary max-w-2xl mx-auto mb-8" data-id="element-351">
            Some stories deserve more than 48 hours.
          </p>
        </FadeIn>

        <FadeIn delay={0.8} className="relative w-full max-w-md md:max-w-lg mx-auto mb-8" data-id="element-352">
          <div className="relative aspect-[4/3] w-full flex justify-center" data-id="element-353">
            <div className="rounded-3xl overflow-hidden" data-id="element-354">
              <img src="https://cdn.magicpatterns.com/uploads/bx8zXpS1o37kRzPKWiqoqb/mockup.png" alt="The Voice They Couldn't Silence Book Mockup" className="w-full h-full object-cover drop-shadow-2xl" data-id="element-355" />
            </div>
          </div>
        </FadeIn>

        <FadeIn delay={1.0} data-id="element-356">
          <p className="font-body text-xl md:text-2xl text-text-primary mb-6 max-w-lg mx-auto" data-id="element-357">
            Be the person who knows the full story.
          </p>
          <Button href="#offer" className="mb-6" data-id="element-358">
            Get The Memorial Edition &rarr;
          </Button>

          {/* Urgency Countdown */}
          <div className="mb-6" data-id="element-359">
            <p className="font-ui text-xs font-semibold tracking-widest uppercase text-accent-soft mb-3" data-id="element-360">
              Memorial Edition Closes In
            </p>
            <div className="inline-flex items-center gap-3 md:gap-4 bg-elevated/60 border border-border-default rounded-sm px-5 py-3 shadow-gold-subtle" data-id="element-361">
              <div className="flex flex-col items-center" data-id="element-362">
                <span className="font-mono text-2xl md:text-3xl font-bold text-accent-primary leading-none tabular-nums" data-id="element-363">
                  {countdown.hours}
                </span>
                <span className="font-ui text-[9px] uppercase tracking-widest text-text-muted mt-1" data-id="element-364">
                  Hours
                </span>
              </div>
              <span className="font-mono text-xl text-accent-primary/50 -mt-3" data-id="element-365">
                :
              </span>
              <div className="flex flex-col items-center" data-id="element-366">
                <span className="font-mono text-2xl md:text-3xl font-bold text-accent-primary leading-none tabular-nums" data-id="element-367">
                  {countdown.minutes}
                </span>
                <span className="font-ui text-[9px] uppercase tracking-widest text-text-muted mt-1" data-id="element-368">
                  Min
                </span>
              </div>
              <span className="font-mono text-xl text-accent-primary/50 -mt-3" data-id="element-369">
                :
              </span>
              <div className="flex flex-col items-center" data-id="element-370">
                <span className="font-mono text-2xl md:text-3xl font-bold text-accent-primary leading-none tabular-nums" data-id="element-371">
                  {countdown.seconds}
                </span>
                <span className="font-ui text-[9px] uppercase tracking-widest text-text-muted mt-1" data-id="element-372">
                  Sec
                </span>
              </div>
            </div>
          </div>

          <p className="font-ui text-xs text-text-muted tracking-wide" data-id="element-373">
            Instant digital access. Download in seconds.
          </p>

          {/* Social proof */}
          <div className="flex flex-col items-center gap-2 mt-6" data-id="element-374">
            <div className="flex items-center gap-1" data-id="element-375">
              {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 text-accent-primary fill-accent-primary" data-id="element-376" />)}
            </div>
            <p className="font-ui text-xs text-text-muted tracking-wide" data-id="element-377">
              2,400+ readers · Rated 4.9/5
            </p>
          </div>
        </FadeIn>
      </div>

      <FadeIn delay={1.2} className="absolute bottom-8 left-1/2 -translate-x-1/2" data-id="element-378">
        <button onClick={scrollToNext} className="text-text-muted hover:text-accent-primary transition-colors duration-300 animate-bounce" aria-label="Scroll down" data-id="element-379">
          <ArrowDown className="w-6 h-6" data-id="element-380" />
        </button>
      </FadeIn>
    </section>;
}